/* ==========================================================================
   Scirocco — theme.js (core)
   API condivise per tutte le aree del tema. Vanilla JS, nessuna libreria.

   window.SC:
     SC.config                      → window.SC_CONFIG (snippet sc-config)
     SC.$ / SC.$$                   → querySelector / querySelectorAll (array)
     SC.on(evt, fn) / SC.emit(evt, detail)
     SC.define(tag, Class)          → customElements.define idempotente
     SC.money(cents)                → "59 €" (it/es) come lo snippet money
     SC.fetchSections(ids, url)     → { id: html } via Section Rendering API
     SC.sections.swap(map)          → sostituisce [data-sc-swap="key"] nelle sezioni (o l'intera sezione)
     SC.cart.add(items, opts)       → POST cart/add.js (+ sezioni) → apre il drawer
     SC.cart.change({key, quantity})→ POST cart/change.js per chiave di riga
     SC.cart.update({key: qty, ...}) → POST cart/update.js (più righe insieme)
     SC.cart.refresh()              → ri-renderizza drawer, contatore e pagina carrello
     SC.dialog.open(id, {trigger}) / SC.dialog.close(id) / SC.dialog.toggle(id)
     SC.toast(message, {variant, action: {label, href}, duration})
     SC.track(name, params, {standard}) → Meta Pixel con consenso (vedi SC.pixel)
   Eventi: sc:cart:updated {itemCount, source, added}, sc:cart:error {status, message, source},
           sc:dialog:open {id}, sc:dialog:close {id}, sc:dom:updated {root}, sc:consent {marketing}
   ========================================================================== */
(() => {
  'use strict';

  const CFG = window.SC_CONFIG || { routes: {}, settings: {}, strings: {} };
  const SC = (window.SC = window.SC || {});
  SC.config = CFG;

  /* ---------- utilità ---------- */
  const $ = (sel, root = document) => root.querySelector(sel);
  const $$ = (sel, root = document) => Array.from(root.querySelectorAll(sel));
  SC.$ = $;
  SC.$$ = $$;
  SC.emit = (name, detail = {}) => document.dispatchEvent(new CustomEvent(name, { detail }));
  SC.on = (name, fn) => document.addEventListener(name, fn);
  SC.define = (tag, cls) => {
    if (!customElements.get(tag)) customElements.define(tag, cls);
  };
  SC.debounce = (fn, ms = 200) => {
    let t;
    return (...args) => {
      clearTimeout(t);
      t = setTimeout(() => fn(...args), ms);
    };
  };
  SC.reduced = () => window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  SC.isDesktop = () => window.matchMedia('(min-width: 990px)').matches;
  const routes = CFG.routes || {};
  const url = (key, suffix = '') => (routes[key] || '/') + suffix;

  /* ---------- prezzi ---------- */
  SC.money = (cents) => {
    const value = Number(cents || 0) / 100;
    const currency = CFG.currency || 'EUR';
    const locale = CFG.locale || 'it';
    const whole = Number.isInteger(value);
    if (currency === 'EUR' && locale !== 'en') {
      const n = value.toLocaleString(locale === 'es' ? 'es-ES' : 'it-IT', {
        minimumFractionDigits: whole ? 0 : 2,
        maximumFractionDigits: 2,
      });
      return `${n} €`;
    }
    try {
      return new Intl.NumberFormat(locale, {
        style: 'currency',
        currency,
        minimumFractionDigits: whole ? 0 : 2,
      }).format(value);
    } catch (e) {
      return `${value.toFixed(2)} ${currency}`;
    }
  };

  /* ---------- Section Rendering API ---------- */
  SC.fetchSections = async (ids, pageUrl = window.location.pathname) => {
    const list = Array.from(new Set(ids)).slice(0, 5).join(',');
    const sep = pageUrl.includes('?') ? '&' : '?';
    const res = await fetch(`${pageUrl}${sep}sections=${encodeURIComponent(list)}`, {
      headers: { Accept: 'application/json' },
    });
    if (!res.ok) throw new Error(`sections ${res.status}`);
    return res.json();
  };

  SC.sections = {
    /** Sostituisce il contenuto delle sezioni restituite dalla SRA.
     *  Se la sezione contiene [data-sc-swap="chiave"], sostituisce solo quegli elementi
     *  (così un <dialog> aperto resta aperto); altrimenti l'intero contenuto. */
    swap(map) {
      if (!map) return;
      Object.entries(map).forEach(([id, html]) => {
        if (typeof html !== 'string') return;
        const doc = new DOMParser().parseFromString(html, 'text/html');
        if (id === 'cart-count') {
          const n = doc.querySelector('[data-sc-cart-count]');
          if (n) SC.cart.setCount(parseInt(n.textContent, 10) || 0);
          return;
        }
        const current = document.getElementById(`shopify-section-${id}`);
        if (!current) return;
        const targets = $$('[data-sc-swap]', current);
        if (targets.length) {
          targets.forEach((el) => {
            const key = el.getAttribute('data-sc-swap');
            const next = doc.querySelector(`[data-sc-swap="${CSS.escape(key)}"]`);
            if (next) el.replaceWith(document.importNode(next, true));
          });
        } else {
          const next = doc.getElementById(`shopify-section-${id}`);
          if (next) current.innerHTML = next.innerHTML;
        }
        SC.emit('sc:dom:updated', { root: current });
      });
    },
  };

  /* ---------- Carrello ---------- */
  SC.cart = {
    count: (CFG.cart && CFG.cart.count) || 0,

    sectionIds() {
      const ids = ['cart-drawer', 'cart-count'];
      $$('[data-sc-cart-section]').forEach((el) => {
        const id = el.getAttribute('data-sc-cart-section');
        if (id && !ids.includes(id)) ids.push(id);
      });
      return ids.slice(0, 5);
    },

    setCount(n) {
      const prev = this.count;
      this.count = n;
      $$('[data-sc-cart-count]').forEach((el) => {
        el.textContent = n;
      });
      $$('[data-sc-cart-empty]').forEach((el) => {
        el.toggleAttribute('data-empty', n === 0);
      });
      if (n !== prev && SC.motion && SC.motion.bump) {
        $$('[data-sc-cart-icon]').forEach((el) => SC.motion.bump(el));
      }
    },

    async post(path, body) {
      let res;
      try {
        res = await fetch(path, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify(body),
        });
      } catch (e) {
        // rete assente/instabile: niente "Failed to fetch" in inglese nel toast
        const err = new Error((CFG.strings && CFG.strings.cartError) || 'Errore');
        err.status = 0;
        throw err;
      }
      let data = {};
      try {
        data = await res.json();
      } catch (e) {
        data = {};
      }
      if (!res.ok || data.status) {
        const err = new Error(data.description || data.message || (CFG.strings && CFG.strings.cartError) || 'Errore');
        err.status = res.status || data.status;
        throw err;
      }
      return data;
    },

    /**
     * Aggiunge articoli. items: [{ id: variantId, quantity: 1, properties?: {} }]
     * opts: { source: 'pdp'|'bundle'|'upsell'|..., open: bool (default: in base a cart_type), toast: bool }
     * Ritorna la risposta di /cart/add.js ({ items: [...] , sections: {...} }).
     */
    async add(items, opts = {}) {
      const source = opts.source || 'unknown';
      const cartType = (CFG.settings && CFG.settings.cartType) || 'drawer';
      try {
        const data = await this.post(url('cart_add', '.js'), {
          items: items.map((i) => {
            const out = { id: Number(i.id), quantity: Number(i.quantity || 1) };
            if (i.properties) out.properties = i.properties;
            return out;
          }),
          sections: this.sectionIds().join(','),
          sections_url: window.location.pathname,
        });
        if (data.sections) SC.sections.swap(data.sections);
        const added = data.items || [];
        SC.emit('sc:cart:updated', { itemCount: this.count, source, added });
        const open = opts.open !== undefined ? opts.open : cartType === 'drawer';
        if (cartType === 'page' && opts.open === undefined) {
          window.location.href = url('cart');
        } else if (open) {
          SC.dialog.open('cart');
        } else if (opts.toast !== false) {
          SC.toast((CFG.strings && CFG.strings.toastAdded) || 'Nel carrello.', {
            action: { label: (CFG.strings && CFG.strings.toastView) || 'Vedi carrello', href: url('cart'), open: 'cart' },
          });
        }
        return data;
      } catch (err) {
        SC.emit('sc:cart:error', { status: err.status, message: err.message, source });
        SC.toast(err.message, { variant: 'error' });
        throw err;
      }
    },

    /** Cambia la quantità di una riga per CHIAVE (mai per indice). quantity 0 = rimuovi.
     *  opts: { source, recover: false = non fa il refresh automatico dopo un errore } */
    /** Cambia UNA riga: per chiave ({ key }) oppure per posizione 1..n ({ line }).
     *  La posizione serve come ultima spiaggia quando le chiavi non valgono più. */
    async change({ key, line, quantity }, opts = {}) {
      const source = opts.source || 'cart';
      const target = line ? { line: Number(line) } : { id: key };
      try {
        const data = await this.post(url('cart_change', '.js'), {
          ...target,
          quantity: Number(quantity),
          sections: this.sectionIds().join(','),
          sections_url: window.location.pathname,
        });
        if (data.sections) SC.sections.swap(data.sections);
        if (typeof data.item_count === 'number') this.setCount(data.item_count);
        SC.emit('sc:cart:updated', { itemCount: this.count, source, cart: data });
        return data;
      } catch (err) {
        SC.emit('sc:cart:error', { status: err.status, message: err.message, source });
        if (opts.toast !== false) SC.toast(err.message, { variant: 'error' });
        // recover: false → il chiamante (la coda di cart.js) aspetta lui stesso il refresh prima del passo successivo
        if (opts.recover !== false) SC.cart.refresh();
        throw err;
      }
    },

    /** Aggiorna più righe in una sola richiesta: updates = { "<chiave riga>": quantità, ... }.
     *  Serve quando lo stesso modello è diviso su più righe (lo sconto "2° paio" le separa). */
    async update(updates, opts = {}) {
      const source = opts.source || 'cart';
      try {
        const data = await this.post(url('cart_update', '.js'), {
          updates,
          sections: this.sectionIds().join(','),
          sections_url: window.location.pathname,
        });
        if (data.sections) SC.sections.swap(data.sections);
        if (typeof data.item_count === 'number') this.setCount(data.item_count);
        SC.emit('sc:cart:updated', { itemCount: this.count, source, cart: data });
        return data;
      } catch (err) {
        SC.emit('sc:cart:error', { status: err.status, message: err.message, source });
        // toast: false → chi chiama ha un piano B e non vuole allarmare per un tentativo intermedio
        if (opts.toast !== false) SC.toast(err.message, { variant: 'error' });
        // recover: false → il chiamante (la coda di cart.js) aspetta lui stesso il refresh prima del passo successivo
        if (opts.recover !== false) SC.cart.refresh();
        throw err;
      }
    },

    async refresh() {
      try {
        const map = await SC.fetchSections(this.sectionIds());
        SC.sections.swap(map);
        SC.emit('sc:cart:updated', { itemCount: this.count, source: 'refresh' });
      } catch (e) {
        /* offline o sessione scaduta: nessuna azione */
      }
    },

    async get() {
      const res = await fetch(url('cart', '.js'), { headers: { Accept: 'application/json' } });
      return res.json();
    },
  };

  // Tasto "indietro" con pagina in cache: il carrello potrebbe essere cambiato.
  window.addEventListener('pageshow', (e) => {
    if (e.persisted) SC.cart.refresh();
  });

  /* ---------- Dialog (drawer, sheet, menu, modale) ---------- */
  SC.dialog = {
    stack: [],
    get(id) {
      return document.getElementById(`sc-dialog-${id}`);
    },
    isOpen(id) {
      const d = this.get(id);
      return !!(d && d.open);
    },
    open(id, { trigger } = {}) {
      const dlg = this.get(id);
      if (!dlg) return false;
      // riaperto durante l'animazione di chiusura: annulliamo la chiusura e torna aperto
      const closing = dlg.open && dlg.classList.contains('is-closing');
      if (dlg.open && !closing) return true;
      clearTimeout(dlg._scCloseTimer);
      // un solo pannello alla volta (tranne i figli espliciti)
      this.stack.slice().forEach((openId) => {
        if (openId !== id && !dlg.hasAttribute('data-sc-stack')) this.close(openId, { restoreFocus: false });
      });
      if (trigger || !closing) dlg._scTrigger = trigger || document.activeElement;
      document.documentElement.classList.add('sc-lock');
      dlg.classList.remove('is-closing');
      if (!closing) {
        try {
          dlg.showModal();
        } catch (e) {
          dlg.setAttribute('open', '');
        }
        // forza il reflow così la transizione di ingresso parte
        void dlg.offsetWidth;
        if (!this.stack.includes(id)) this.stack.push(id);
      }
      requestAnimationFrame(() => dlg.classList.add('is-open'));
      $$(`[data-sc-open="${id}"]`).forEach((b) => b.setAttribute('aria-expanded', 'true'));
      const focusTarget = dlg.querySelector('[autofocus], [data-sc-autofocus]');
      if (focusTarget) setTimeout(() => focusTarget.focus({ preventScroll: true }), 60);
      // la chiusura annullata non ha mai emesso sc:dialog:close: niente secondo sc:dialog:open
      if (!closing) SC.emit('sc:dialog:open', { id });
      return true;
    },
    close(id, { restoreFocus = true } = {}) {
      const dlg = this.get(id);
      if (!dlg || !dlg.open || dlg.classList.contains('is-closing')) return;
      dlg.classList.remove('is-open');
      dlg.classList.add('is-closing');
      const done = () => {
        dlg.classList.remove('is-closing');
        // fuori dalla pila prima di close(): l'evento nativo "close" vede che la pulizia è già fatta
        this.stack = this.stack.filter((x) => x !== id);
        try {
          dlg.close();
        } catch (e) {
          dlg.removeAttribute('open');
        }
        this.cleanup(id, dlg, restoreFocus);
      };
      if (SC.reduced()) done();
      else dlg._scCloseTimer = setTimeout(done, 300);
    },
    /** Pulizia dopo la chiusura (animata o nativa): pila, blocco scroll, aria-expanded, focus, evento. */
    cleanup(id, dlg, restoreFocus) {
      this.stack = this.stack.filter((x) => x !== id);
      if (!document.querySelector('dialog.sc-dialog[open]')) document.documentElement.classList.remove('sc-lock');
      $$(`[data-sc-open="${id}"]`).forEach((b) => b.setAttribute('aria-expanded', 'false'));
      if (restoreFocus && dlg._scTrigger && document.contains(dlg._scTrigger)) {
        try {
          dlg._scTrigger.focus({ preventScroll: true });
        } catch (e) {}
      }
      SC.emit('sc:dialog:close', { id });
    },
    toggle(id, opts) {
      const dlg = this.get(id);
      // in chiusura animata conta come chiuso: il click lo riapre
      return dlg && dlg.open && !dlg.classList.contains('is-closing') ? this.close(id) : this.open(id, opts);
    },
  };

  document.addEventListener('click', (e) => {
    const opener = e.target.closest('[data-sc-open]');
    if (opener) {
      const id = opener.getAttribute('data-sc-open');
      // il carrello in modalità "pagina" segue il link
      if (id === 'cart' && CFG.settings && CFG.settings.cartType === 'page' && opener.tagName === 'A') return;
      if (SC.dialog.get(id)) {
        e.preventDefault();
        SC.dialog.toggle(id, { trigger: opener });
      }
      return;
    }
    const closer = e.target.closest('[data-sc-close]');
    if (closer) {
      const dlg = closer.closest('dialog.sc-dialog');
      const id = closer.getAttribute('data-sc-close') || (dlg && dlg.id.replace('sc-dialog-', ''));
      if (id) {
        e.preventDefault();
        SC.dialog.close(id);
      }
      return;
    }
    // click sullo sfondo (fuori dal pannello) chiude
    if (e.target.matches && e.target.matches('dialog.sc-dialog[open]')) {
      SC.dialog.close(e.target.id.replace('sc-dialog-', ''));
    }
  });

  // ESC: anima la chiusura invece di chiudere di colpo
  document.addEventListener(
    'cancel',
    (e) => {
      const dlg = e.target;
      if (dlg && dlg.matches && dlg.matches('dialog.sc-dialog')) {
        e.preventDefault();
        SC.dialog.close(dlg.id.replace('sc-dialog-', ''));
      }
    },
    true
  );

  // Chiusura nativa (doppio Esc, form method="dialog", dialog.close() di altri script):
  // stessa pulizia di SC.dialog.close. Se il dialog non è più nella pila l'ha già gestita close().
  document.addEventListener(
    'close',
    (e) => {
      const dlg = e.target;
      if (!dlg || !dlg.matches || !dlg.matches('dialog.sc-dialog') || dlg.open) return;
      const id = dlg.id.replace('sc-dialog-', '');
      if (!SC.dialog.stack.includes(id)) return;
      clearTimeout(dlg._scCloseTimer);
      dlg.classList.remove('is-open', 'is-closing');
      SC.dialog.cleanup(id, dlg, true);
    },
    true
  );

  /* ---------- Toast (popover: resta sopra anche ai dialog modali) ---------- */
  // Per i lettori di schermo: regioni live fisse e nascoste, create alla prima occorrenza.
  // Il popover è display:none quando ci scriviamo dentro, quindi da solo spesso non viene letto.
  const live = {};
  let liveTimer;
  const clearLive = () => {
    clearTimeout(liveTimer);
    Object.values(live).forEach((r) => (r.textContent = ''));
  };
  const announce = (message, assertive) => {
    const role = assertive ? 'alert' : 'status';
    if (!live[role]) {
      const r = document.createElement('div');
      r.className = 'sc-vh';
      r.setAttribute('role', role);
      r.setAttribute('aria-live', assertive ? 'assertive' : 'polite');
      r.setAttribute('aria-atomic', 'true');
      live[role] = r;
    }
    // con un dialog modale aperto il resto della pagina è inerte (anche per i lettori di schermo):
    // la regione va dentro il dialog in cima alla pila
    const top = SC.dialog.stack
      .map((id) => SC.dialog.get(id))
      .filter((d) => d && d.open && !d.classList.contains('is-closing'))
      .pop();
    const host = top || document.body;
    const region = live[role];
    if (region.parentNode !== host) host.appendChild(region);
    // svuota e riscrive poco dopo: così lo stesso messaggio ripetuto viene riletto
    clearLive();
    liveTimer = setTimeout(() => {
      region.textContent = message;
    }, 100);
  };

  let toastTimer;
  let toastHideTimer;
  SC.toast = (message, { variant = 'success', action = null, duration = 3200 } = {}) => {
    const el = document.getElementById('sc-toast');
    if (!el) return;
    const text = el.querySelector('[data-sc-toast-text]');
    const act = el.querySelector('[data-sc-toast-action]');
    if (text) text.textContent = message;
    if (message) announce(message, variant === 'error');
    el.dataset.variant = variant;
    if (act) {
      if (action && action.label) {
        act.textContent = action.label;
        act.href = action.href || url('cart');
        act.hidden = false;
        act.onclick = (ev) => {
          if (action.open && SC.dialog.get(action.open) && !(CFG.settings && CFG.settings.cartType === 'page')) {
            ev.preventDefault();
            hide();
            SC.dialog.open(action.open);
          }
        };
      } else {
        act.hidden = true;
      }
    }
    function hide() {
      el.classList.remove('is-in');
      clearLive();
      toastHideTimer = setTimeout(() => {
        try {
          el.hidePopover();
        } catch (e) {}
      }, 300);
    }
    // un toast nuovo non deve essere nascosto dalla chiusura ritardata di quello prima
    clearTimeout(toastHideTimer);
    try {
      if (el.matches(':popover-open')) el.hidePopover();
      el.showPopover();
    } catch (e) {
      el.style.display = 'flex';
    }
    requestAnimationFrame(() => el.classList.add('is-in'));
    clearTimeout(toastTimer);
    toastTimer = setTimeout(hide, duration);
  };

  /* ---------- Meta Pixel (eventi personalizzati, con consenso) ---------- */
  const PX = (CFG.settings && CFG.settings.pixel) || {};
  SC.pixel = {
    loaded: false,
    revoked: false, // consenso negato o ritirato: nessun evento, anche con fbq già caricato
    queue: [],
    consent() {
      if (!PX.gating) return true;
      const p = window.Shopify && window.Shopify.customerPrivacy;
      if (!p) return false;
      try {
        // serve anche il via libera alla "vendita dei dati" (opt-out CCPA e simili)
        const sale = typeof p.saleOfDataAllowed !== 'function' || !!p.saleOfDataAllowed();
        if (typeof p.marketingAllowed === 'function') return !!p.marketingAllowed() && sale;
        if (typeof p.userCanBeTracked === 'function') return !!p.userCanBeTracked() && sale;
      } catch (e) {}
      return false;
    },
    load() {
      if (this.loaded || !PX.enabled || !PX.id) return;
      this.loaded = true;
      /* eslint-disable */
      if (typeof window.fbq === 'undefined') {
        !(function (f, b, e, v, n, t, s) {
          if (f.fbq) return;
          n = f.fbq = function () {
            n.callMethod ? n.callMethod.apply(n, arguments) : n.queue.push(arguments);
          };
          if (!f._fbq) f._fbq = n;
          n.push = n;
          n.loaded = !0;
          n.version = '2.0';
          n.queue = [];
          t = b.createElement(e);
          t.async = !0;
          t.src = v;
          s = b.getElementsByTagName(e)[0];
          s.parentNode.insertBefore(t, s);
        })(window, document, 'script', 'https://connect.facebook.net/en_US/fbevents.js');
      }
      /* eslint-enable */
      // niente eventi automatici del Pixel (clic sui pulsanti, microdati): solo quelli inviati da noi
      window.fbq('set', 'autoConfig', false, PX.id);
      window.fbq('init', PX.id);
      if (PX.pageview) window.fbq('track', 'PageView');
      this.queue.splice(0).forEach(([name, params, standard]) => this.send(name, params, standard));
    },
    send(name, params, standard) {
      if (this.revoked || typeof window.fbq !== 'function') return;
      window.fbq(standard ? 'track' : 'trackCustom', name, params || {});
    },
    track(name, params = {}, standard = false) {
      if (!PX.enabled || !PX.id || this.revoked) return;
      if (this.loaded) {
        this.send(name, params, standard);
        return;
      }
      this.queue.push([name, params, standard]);
      // coda in attesa del consenso limitata: restano gli ultimi 20 eventi
      if (this.queue.length > 20) this.queue.shift();
    },
    init() {
      if (!PX.enabled || !PX.id || CFG.designMode) return;
      const tryLoad = () => {
        if (this.consent()) this.load();
      };
      if (!PX.gating) {
        this.load();
        return;
      }
      if (window.Shopify && typeof window.Shopify.loadFeatures === 'function') {
        window.Shopify.loadFeatures([{ name: 'consent-tracking-api', version: '0.1' }], (err) => {
          if (!err) tryLoad();
        });
      } else {
        tryLoad();
      }
      document.addEventListener('visitorConsentCollected', (ev) => {
        const d = ev.detail || {};
        const allowed = !!d.marketingAllowed && d.saleOfDataAllowed !== false;
        SC.emit('sc:consent', { marketing: !!d.marketingAllowed });
        const fbqReady = this.loaded && typeof window.fbq === 'function';
        if (allowed) {
          // consenso (ri)dato: si riparte
          if (this.revoked && fbqReady) window.fbq('consent', 'grant');
          this.revoked = false;
          this.load();
        } else {
          // negato o ritirato: da qui in poi nessun evento, la coda in attesa si butta
          this.revoked = true;
          this.queue.length = 0;
          if (fbqReady) window.fbq('consent', 'revoke');
        }
      });
    },
  };
  SC.track = (name, params = {}, opts = {}) => SC.pixel.track(name, params, !!opts.standard);

  /* eventi automatici ereditati dal tema v18 (senza PageView/Search, già inviati dal canale Facebook) */
  function autoEvents() {
    const t = (CFG.template && CFG.template.name) || '';
    const suffix = (CFG.template && CFG.template.suffix) || '';
    const path = window.location.pathname.toLowerCase();
    if (PX.pageview && t === 'search') {
      const q = new URLSearchParams(window.location.search).get('q');
      if (q) SC.track('Search', { search_string: q, content_category: 'site_search' }, { standard: true });
    }
    if (t === 'collection') {
      const name = (document.querySelector('h1') || {}).textContent || document.title;
      SC.track('ViewCategory', { content_name: name.trim(), content_category: name.trim(), content_type: 'product_group' });
    }
    if (t === 'page' && (suffix === 'contact' || path.includes('/contact'))) SC.track('ContactPageView', { content_name: 'Contact Page' });
    if (t === 'page' && (suffix.indexOf('chi-siamo') === 0 || path.includes('/chi-siamo'))) SC.track('AboutPageView', { content_name: 'About Page' });
    if (t === 'cart') SC.track('ViewCart', { content_type: 'product' });
    if (t === 'product' && CFG.product) {
      SC.track('ViewContentEnriched', {
        content_name: CFG.product.title,
        content_ids: [String(CFG.product.id)],
        value: (CFG.product.price || 0) / 100,
        currency: CFG.currency,
        content_type: 'product',
      });
      [15, 30, 60].forEach((sec) => setTimeout(() => SC.track('TimeOnProduct', { seconds: sec, page: path }), sec * 1000));
    }
    const marks = t === 'product' ? [25, 50, 75, 100] : [50, 100];
    const fired = {};
    const onScroll = SC.debounce(() => {
      const h = document.documentElement;
      const max = h.scrollHeight - h.clientHeight;
      if (max <= 0) return;
      const pct = Math.round((window.scrollY / max) * 100);
      marks.forEach((m) => {
        if (pct >= m && !fired[m]) {
          fired[m] = true;
          SC.track('ScrollDepth', { percent: m, page: path });
        }
      });
    }, 150);
    window.addEventListener('scroll', onScroll, { passive: true });

    document.addEventListener('submit', (e) => {
      const form = e.target;
      if (!(form instanceof HTMLFormElement)) return;
      const type = (form.querySelector('input[name="form_type"]') || {}).value || '';
      if (type === 'contact') SC.track('Contact', { content_name: 'Contact Form' }, { standard: true });
      else if (type === 'customer') SC.track('Lead', { content_name: 'Newsletter Signup', content_category: 'email_subscription' }, { standard: true });
    });

    document.addEventListener('click', (e) => {
      const tracked = e.target.closest('[data-sc-track]');
      if (tracked) {
        let params = {};
        try {
          params = JSON.parse(tracked.getAttribute('data-sc-track-params') || '{}');
        } catch (err) {}
        SC.track(tracked.getAttribute('data-sc-track'), Object.assign({ page: path }, params));
      }
      const a = e.target.closest('a[href]');
      if (!a) return;
      const href = (a.getAttribute('href') || '').toLowerCase();
      if (href.includes('wa.me') || href.includes('whatsapp')) SC.track('ClickWhatsApp', { content_name: 'WhatsApp Click', page: path });
      if (href.startsWith('tel:')) SC.track('ClickPhone', { content_name: 'Phone Click', page: path });
      if (href.startsWith('mailto:')) SC.track('ClickEmail', { content_name: 'Email Click', page: path });
      if (/instagram\.com|facebook\.com|tiktok\.com/.test(href)) SC.track('ClickSocial', { platform: href, page: path });
      if (a.hostname && a.hostname !== window.location.hostname) SC.track('OutboundClick', { url: a.href, page: path });
    });
  }

  /* ---------- avvio ---------- */
  function boot() {
    SC.pixel.init();
    if (!CFG.designMode) autoEvents();
    SC.emit('sc:ready', {});
  }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot);
  else boot();
})();
